/**
 * CAD-style snap inference for multi-click room drawing.
 * Pure functions only — no React, no Firebase. See
 * claude-docs/2026-05-28-room-snap-inference-design.md.
 */

import { WALL_THICKNESS } from './wallCreationUtils';
import { getWallEndpoints } from './snapToWall';
import { getEdgeInwardNormal } from './roomPolygonUtils';
import { LINE_SNAP_TOLERANCE_M } from './snapTolerances';

const EPS = 1e-9;
const DEFAULT_LINE_TOLERANCE = LINE_SNAP_TOLERANCE_M; // perpendicular activation distance
const DEFAULT_SEARCH_RADIUS = 1.5; // m, "nearby wall" radius for parallel/perp
const DEFAULT_POINT_TOLERANCE = 0.3048; // m (1 ft), matches VERTEX_SNAP_THRESHOLD

/** Unit vector from a->b, or null if coincident. */
function unit(a, b) {
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  const len = Math.hypot(dx, dz);
  if (len < EPS) return null;
  return { x: dx / len, z: dz / len };
}

/** Perpendicular (left normal) of a unit direction, in the XZ plane. */
function leftNormal(dir) {
  return { x: -dir.z, z: dir.x };
}

/** Project point p onto the infinite line {origin, dir(unit)}. */
export function projectPointOnLine(p, line) {
  const { origin, dir } = line;
  const t = (p.x - origin.x) * dir.x + (p.z - origin.z) * dir.z;
  return { x: origin.x + t * dir.x, z: origin.z + t * dir.z };
}

/** Absolute perpendicular distance from p to the line. */
export function perpDistanceToLine(p, line) {
  const proj = projectPointOnLine(p, line);
  return Math.hypot(p.x - proj.x, p.z - proj.z);
}

/** Signed side of p relative to the line: +1 / -1 / 0. */
export function sideOfLine(p, line) {
  const n = leftNormal(line.dir);
  const d = (p.x - line.origin.x) * n.x + (p.z - line.origin.z) * n.z;
  if (d > EPS) return 1;
  if (d < -EPS) return -1;
  return 0;
}

/** Intersection of two infinite lines, or null if (near-)parallel. */
export function intersectLines(l1, l2) {
  const det = l1.dir.x * -l2.dir.z - l1.dir.z * -l2.dir.x;
  if (Math.abs(det) < EPS) return null;
  const bx = l2.origin.x - l1.origin.x;
  const bz = l2.origin.z - l1.origin.z;
  const t = (bx * -l2.dir.z - bz * -l2.dir.x) / det;
  return { x: l1.origin.x + t * l1.dir.x, z: l1.origin.z + t * l1.dir.z };
}

export { unit, leftNormal, EPS };

/** The 4 footprint corners of a structure wall (local ±halfLen, ±halfThk). */
export function wallFootprintCorners(wall) {
  if (!wall.center || !wall.dimensions) return [];
  const cx = wall.center.x;
  const cz = wall.center.z;
  const halfLen = (wall.dimensions.x ?? 0) / 2;
  const halfThk = (wall.dimensions.z ?? WALL_THICKNESS) / 2;
  const ry = wall.rotation?.y ?? 0;
  const cosR = Math.cos(ry);
  const sinR = Math.sin(ry);
  const offsets = [
    { lx: -halfLen, lz: -halfThk },
    { lx: -halfLen, lz: halfThk },
    { lx: halfLen, lz: -halfThk },
    { lx: halfLen, lz: halfThk },
  ];
  return offsets.map(({ lx, lz }) => ({
    x: cx + lx * cosR + lz * sinR,
    z: cz - lx * sinR + lz * cosR,
  }));
}

/**
 * Collect 0-D snap candidates near the cursor.
 * @returns {{points: Array}}
 */
export function gatherPointConstraints(cursor, walls, rooms) {
  const points = [];

  if (rooms) {
    for (const room of rooms.values()) {
      const corners = room.floors?.[0]?.polygonCorners ?? [];
      for (const c of corners) {
        points.push({
          kind: 'point',
          subtype: 'roomCorner',
          position: { x: c.x, z: c.z },
        });
      }
    }
  }

  if (walls) {
    for (const wall of walls) {
      const corners = wallFootprintCorners(wall);
      if (corners.length !== 4) continue;
      for (const c of corners) {
        points.push({
          kind: 'point',
          subtype: 'wallCorner',
          position: c,
          wallId: wall.id,
          provisional: true,
          candidates: corners,
        });
      }
    }
  }

  return { points };
}

/**
 * A wall's two FACE lines as bounded segments: `{ origin, dir, length, side }`
 * with `origin` at one end of the face and `length` its run, from the same
 * footprint corners the corner snap uses (local ±halfLen, ±halfThk).
 */
export function wallFaceLines(wall) {
  const c = wallFootprintCorners(wall);
  if (c.length !== 4) return [];
  const faces = [];
  // [0]→[2] is the local -Z face, [1]→[3] the local +Z face.
  for (const [a, b, side] of [
    [c[0], c[2], -1],
    [c[1], c[3], 1],
  ]) {
    const dir = unit(a, b);
    if (!dir) continue;
    faces.push({
      origin: { x: a.x, z: a.z },
      dir,
      length: Math.hypot(b.x - a.x, b.z - a.z),
      side,
    });
  }
  return faces;
}

function wallCenterLine(wall) {
  const ep = getWallEndpoints(wall);
  if (!ep) return null;
  const dir = unit(ep.start, ep.end);
  if (!dir) return null;
  return { origin: { x: ep.start.x, z: ep.start.z }, dir };
}

/**
 * Collect 1-D guide lines near the cursor: collinear-with-wall, axis-align
 * (share X / share Z with an existing corner), and parallel / perpendicular to
 * a nearby wall through the previous-vertex anchor.
 * @returns {{lines: Array}}
 */
export function gatherLineConstraints(
  cursor,
  walls,
  rooms,
  anchor,
  {
    lineTolerance = DEFAULT_LINE_TOLERANCE,
    searchRadius = DEFAULT_SEARCH_RADIUS,
  } = {}
) {
  const lines = [];

  // collinear-with-wall + parallel/perp through the anchor
  if (walls) {
    for (const wall of walls) {
      const cl = wallCenterLine(wall);
      if (!cl) continue;

      const distToWall = perpDistanceToLine(cursor, cl);
      if (distToWall <= lineTolerance) {
        lines.push({
          kind: 'line',
          subtype: 'collinear',
          ...cl,
          wallId: wall.id,
        });
      }

      // A point ALONG a wall face — where a drawn wall tees into this one.
      // Bounded to the face's own run: past its end the face is not there,
      // and the collinear / axis guides above already cover the extension.
      for (const face of wallFaceLines(wall)) {
        const t =
          (cursor.x - face.origin.x) * face.dir.x +
          (cursor.z - face.origin.z) * face.dir.z;
        if (t < 0 || t > face.length) continue;
        if (perpDistanceToLine(cursor, face) > lineTolerance) continue;
        lines.push({
          kind: 'line',
          subtype: 'face',
          origin: face.origin,
          dir: face.dir,
          wallId: wall.id,
        });
      }

      if (anchor && distToWall <= searchRadius) {
        lines.push({
          kind: 'line',
          subtype: 'parallel',
          origin: { x: anchor.x, z: anchor.z },
          dir: cl.dir,
          wallId: wall.id,
        });
        lines.push({
          kind: 'line',
          subtype: 'perp',
          origin: { x: anchor.x, z: anchor.z },
          dir: leftNormal(cl.dir),
          wallId: wall.id,
        });
      }
    }
  }

  // axis-align lines through existing corners aligned with the cursor
  const corners = [];
  if (rooms) {
    for (const room of rooms.values()) {
      for (const c of room.floors?.[0]?.polygonCorners ?? []) {
        corners.push({ x: c.x, z: c.z });
      }
    }
  }
  if (walls) {
    for (const wall of walls) corners.push(...wallFootprintCorners(wall));
  }
  for (const c of corners) {
    if (Math.abs(cursor.x - c.x) <= lineTolerance) {
      lines.push({
        kind: 'line',
        subtype: 'axisZ',
        origin: { x: c.x, z: c.z },
        dir: { x: 0, z: 1 },
      });
    }
    if (Math.abs(cursor.z - c.z) <= lineTolerance) {
      lines.push({
        kind: 'line',
        subtype: 'axisX',
        origin: { x: c.x, z: c.z },
        dir: { x: 1, z: 0 },
      });
    }
  }

  return { lines };
}

function nearestPoint(cursor, points, subtype, tol) {
  let best = null;
  let bestD = tol;
  for (const p of points) {
    if (p.subtype !== subtype) continue;
    const d = Math.hypot(cursor.x - p.position.x, cursor.z - p.position.z);
    if (d < bestD) {
      bestD = d;
      best = p;
    }
  }
  return best;
}

function guideFor(line, point) {
  // A guide segment centered on the snap point, 1 m each way along the line.
  const L = 1;
  return {
    from: { x: point.x - line.dir.x * L, z: point.z - line.dir.z * L },
    to: { x: point.x + line.dir.x * L, z: point.z + line.dir.z * L },
    subtype: line.subtype,
  };
}

/**
 * Resolve gathered constraints into a single snap point.
 * Priority: room corner > wall corner (provisional) > two-line intersection
 * > single-line projection > free cursor.
 */
export function resolveSnap(
  cursor,
  { points = [], lines = [] },
  { pointTolerance = DEFAULT_POINT_TOLERANCE } = {}
) {
  // 1. room corner
  const rc = nearestPoint(cursor, points, 'roomCorner', pointTolerance);
  if (rc) {
    return { position: { ...rc.position }, type: 'roomCorner', guides: [] };
  }

  // 2. wall corner (provisional — finalized at close)
  const wc = nearestPoint(cursor, points, 'wallCorner', pointTolerance);
  if (wc) {
    return {
      position: { ...wc.position },
      type: 'wallCorner',
      guides: [],
      provisional: true,
      wallId: wc.wallId,
      candidates: wc.candidates,
    };
  }

  // 3. two-line intersection nearest the cursor
  let bestHit = null;
  let bestPair = null;
  let bestD = Infinity;
  for (let i = 0; i < lines.length; i++) {
    for (let j = i + 1; j < lines.length; j++) {
      const hit = intersectLines(lines[i], lines[j]);
      if (!hit) continue;
      const d = Math.hypot(cursor.x - hit.x, cursor.z - hit.z);
      if (d <= pointTolerance && d < bestD) {
        bestD = d;
        bestHit = hit;
        bestPair = [lines[i], lines[j]];
      }
    }
  }
  if (bestHit) {
    return {
      position: bestHit,
      type: 'intersection',
      guides: [guideFor(bestPair[0], bestHit), guideFor(bestPair[1], bestHit)],
    };
  }

  // 4. single-line projection (closest line)
  let bestLine = null;
  let bestLineD = Infinity;
  for (const line of lines) {
    const d = perpDistanceToLine(cursor, line);
    if (d < bestLineD) {
      bestLineD = d;
      bestLine = line;
    }
  }
  if (bestLine) {
    const pos = projectPointOnLine(cursor, bestLine);
    // A face snap says which wall the point is on; the other guides are lines
    // THROUGH something (an anchor, a corner), not on a wall.
    return {
      position: pos,
      type: 'line',
      guides: [guideFor(bestLine, pos)],
      ...(bestLine.subtype === 'face' ? { wallId: bestLine.wallId } : {}),
    };
  }

  // 5. free
  return { position: { x: cursor.x, z: cursor.z }, type: 'free', guides: [] };
}

function plain(v) {
  return { x: v.x, z: v.z };
}

// Edge alignment tolerances and a sanity cap on how far resolution may move a
// vertex from where it was drawn.
const DEFAULT_EDGE_ANGLE_TOL = 0.18; // |sin θ| between edge and wall (~10°)
const DEFAULT_EDGE_PERP_TOL = WALL_THICKNESS; // edge within ~a thickness of centerline
const MAX_RESOLVE_MOVE = 0.5; // m

/**
 * Find the existing wall that a polygon edge a→b runs along: (near-)parallel
 * and within `perpTol` of the wall's centerline. Returns the closest such wall
 * object, or null. Used both to align edges and to inherit an adjacent wall's
 * height.
 */
export function findAlignedWall(
  a,
  b,
  walls,
  { angleTol = DEFAULT_EDGE_ANGLE_TOL, perpTol = DEFAULT_EDGE_PERP_TOL } = {}
) {
  const eDir = unit(a, b);
  if (!eDir || !walls) return null;
  const mid = { x: (a.x + b.x) / 2, z: (a.z + b.z) / 2 };

  let best = null;
  let bestPerp = Infinity;
  for (const w of walls) {
    const ep = getWallEndpoints(w);
    if (!ep) continue;
    const wDir = unit(ep.start, ep.end);
    if (!wDir) continue;
    // Parallel? (cross product of unit directions ≈ 0)
    if (Math.abs(eDir.x * wDir.z - eDir.z * wDir.x) > angleTol) continue;
    const cl = { origin: { x: ep.start.x, z: ep.start.z }, dir: wDir };
    const perp = perpDistanceToLine(mid, cl);
    if (perp > perpTol) continue;
    if (perp < bestPerp) {
      bestPerp = perp;
      best = w;
    }
  }
  return best;
}

/**
 * The height a new room should adopt from the existing wall(s) it shares.
 * A room is usually drawn against one existing wall (the collinear edge
 * `findAlignedWall` identifies); the whole room should match that wall's
 * height. Returns the median `dimensions.y` of every existing wall an edge
 * runs along, or null when the room shares no wall (the caller then uses the
 * default height — NOT the structure median).
 *
 * @param {Array<{x:number,z:number}>} vertices - room polygon vertices
 * @param {Array} walls - existing structure walls
 * @returns {number|null}
 */
export function sharedWallHeight(vertices, walls, opts) {
  const n = vertices.length;
  if (n < 2 || !walls || walls.length === 0) return null;
  const heights = [];
  for (let i = 0; i < n; i++) {
    const w = findAlignedWall(vertices[i], vertices[(i + 1) % n], walls, opts);
    const h = w?.dimensions?.y;
    if (h != null) heights.push(h);
  }
  if (heights.length === 0) return null;
  heights.sort((a, b) => a - b);
  const mid = Math.floor(heights.length / 2);
  return heights.length % 2 === 0
    ? (heights[mid - 1] + heights[mid]) / 2
    : heights[mid];
}

/**
 * For a polygon edge a→b, return the room-interior FACE line of the wall it
 * runs along (centerline shifted half a thickness toward the edge's
 * winding-based inward normal), or null when no wall aligns.
 */
function edgeWallFaceLine(a, b, walls, inwardNormal, angleTol, perpTol) {
  const w = findAlignedWall(a, b, walls, { angleTol, perpTol });
  if (!w) return null;

  const cl = wallCenterLine(w);
  if (!cl) return null;
  const mid = { x: (a.x + b.x) / 2, z: (a.z + b.z) / 2 };
  const half = (w.dimensions?.z ?? WALL_THICKNESS) / 2;
  const nrm = leftNormal(cl.dir);
  // Shift toward the room interior of THIS edge, via the polygon's winding-based
  // inward normal — the SAME basis edgeToWallParams offsets the new wall by
  // (getEdgeOutwardNormal = -inward). A consistent basis guarantees the
  // snap-to-face and the wall offset cancel onto the existing centerline.
  // The global polygon centroid is not a safe proxy: for a non-convex room it
  // can land on an edge's outward side, flipping the face and jogging the new
  // wall one thickness off the existing wall.
  const sign = nrm.x * inwardNormal.x + nrm.z * inwardNormal.z >= 0 ? 1 : -1;
  const foot = projectPointOnLine(mid, cl);
  return {
    origin: {
      x: foot.x + nrm.x * half * sign,
      z: foot.z + nrm.z * half * sign,
    },
    dir: cl.dir,
  };
}

/**
 * Snap a finished room polygon onto the walls it was drawn against.
 *
 * Resolution is per-EDGE, not per-vertex: each edge picks the single existing
 * wall it runs along and adopts that wall's interior-face line; every vertex
 * then resolves to the intersection of its two adjacent edges' target lines.
 * Because both endpoints of an edge share one line, an edge can never come out
 * slanted, and a corner shared by two walls lands exactly where their faces
 * cross. Edges with no aligned wall keep the line through their drawn endpoints,
 * so free-drawn vertices are preserved.
 *
 * @param {Array<{x:number,z:number}>} vertices - drawn polygon vertices
 * @param {Array} walls - existing structure walls
 * @returns {Array<{x:number,z:number}>}
 */
export function resolvePolygonToWalls(
  vertices,
  walls,
  { angleTol = DEFAULT_EDGE_ANGLE_TOL, perpTol = DEFAULT_EDGE_PERP_TOL } = {}
) {
  const n = vertices.length;
  if (n < 3 || !walls || walls.length === 0) {
    return vertices.map(plain);
  }

  // Target line for each edge i = (v[i] → v[i+1]).
  const lines = [];
  for (let i = 0; i < n; i++) {
    const a = vertices[i];
    const b = vertices[(i + 1) % n];
    const inward = getEdgeInwardNormal(vertices, i);
    const face = edgeWallFaceLine(a, b, walls, inward, angleTol, perpTol);
    lines[i] = face ?? {
      origin: { x: a.x, z: a.z },
      dir: unit(a, b) ?? { x: 1, z: 0 },
    };
  }

  // Vertex i is the meeting of edge (i-1→i) and edge (i→i+1).
  return vertices.map((v, i) => {
    const drawn = plain(v);
    const hit = intersectLines(lines[(i - 1 + n) % n], lines[i]);
    if (
      hit &&
      Math.hypot(hit.x - drawn.x, hit.z - drawn.z) <= MAX_RESOLVE_MOVE
    ) {
      return hit;
    }
    return drawn;
  });
}
